
interface CountryMapping {
  response: string[];
  request?: string;
}

/**
 * key: OUR side - country name as returned by our API (dropdown name)
 *
 * Example: "Vietnam": { response: ["Viet Nam", "704"], request: "Vietnam" }
 *   ours = "Vietnam", Melissa returns "Viet Nam" (filter), request sends "Vietnam",
 *   "704" is just an extra recognised value.
 */
export const COUNTRY_MAPPINGS: Record<string, CountryMapping> = {
  "U.S.A.": { response: ["United States Of America", "USA", "United States", "840"] },
  "Great Britain": { response: ["United Kingdom", "UK", "England", "Britain", "826"] },
  "South Korea": {
    response: ["Korea, Republic Of", "South Corea", "410"],
    request: "South Korea",
  },
  "North Korea": {
    response: ["Korea, Democratic People's Republic Of", "North Corea", "408"],
    request: "North Korea",
  },
  "United Arabic Emirat": { response: ["United Arab Emirates", "784"], request: "ARE" },
  Arabia: { response: ["Saudi Arabia", "682"] },
  Russia: { response: ["Russian Federation", "643"], request: "Russia" },
  Vietnam: { response: ["Viet Nam", "704"], request: "Vietnam" },
  Taiwan: { response: ["Taiwan, Province Of China", "158"], request: "Taiwan" },
  Malaisia: { response: ["Malaysia", "458"] },
  Bahrein: { response: ["Bahrain", "048"] },
  Andorre: { response: ["Andorra", "020"] },
  Slovaquia: { response: ["Slovakia", "703"] },
  Bosnia: { response: ["Bosnia And Herzegovina", "070"], request: "BA" },
  Brasil: { response: ["Brazil", "076"] },
  "Czech Republic": { response: ["Czechia", "203"], request: "CZ" },
  Danemark: { response: ["Denmark", "208"] },
  Equator: { response: ["Ecuador", "218"], request: "EC" },
  Hungria: { response: ["Hungary", "348"] },
  Koweit: { response: ["Kuwait", "414"] },
  "Marshall ilands": { response: ["Marshall Islands", "584"] },
  Micronesia: { response: ["Micronesia, Federated States of", "583"], request: "FM" },
  Holland: { response: ["Netherlands", "528"] },
  "Mariannes Ilands": { response: ["Northern Mariana Islands", "580"] },
  "Porto rico": { response: ["Puerto Rico", "630"] },
  Singapoure: { response: ["Singapore", "702"] },
  Turquia: { response: ["Turkiye", "792"], request: "TR" },
  "Virgin Ilands, U.S.": { response: ["Virgin Islands, U.S.", "850"], request: "VI" }
};

const normalize = (name: string) => name.toLowerCase().replace(/\./g, "").trim();

/** normalized name / alias / request name -> COUNTRY_MAPPINGS key (our name), so any spelling resolves to our country */
const COUNTRY_INDEX = new Map<string, string>(
  Object.entries(COUNTRY_MAPPINGS).flatMap(([country, { response, request }]) =>
    [country, ...response, ...(request ? [request] : [])].map((name): [string, string] => [
      normalize(name),
      country,
    ])
  )
);

const findCountry = (name: string): string | undefined => COUNTRY_INDEX.get(normalize(name));

/** Name Melissa returns for a COUNTRY_MAPPINGS key */
const melissaNameOf = (country: string): string =>
  COUNTRY_MAPPINGS[country]?.response[0] ?? country;

/** Our API / dropdown country name -> name to send to Melissa in the request */
export const getMelissaCountryName = (countryName: string): string => {
  const country = findCountry(countryName);
  if (!country) return countryName;
  return COUNTRY_MAPPINGS[country]?.request ?? melissaNameOf(country);
};

/** Same conversion, applied to the suggestion request payload */
export const getPayloadMelissaCountryName = getMelissaCountryName;

/** Our API / dropdown country name -> `CountryName` Melissa returns (used to filter suggestions) */
export const getResponseMelissaCountryName = (countryName: string): string => {
  const country = findCountry(countryName);
  return country ? melissaNameOf(country) : countryName;
};

/** Melissa country name (or ISO code) -> our API / dropdown country name */
export const getDropdownCountryName = (melissaCountryName: string): string =>
  findCountry(melissaCountryName) ?? melissaCountryName;
